import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ImagePlus, RotateCcw } from 'lucide-react';
import { useTranslation } from '../../../i18n';
import { resolveMediaUrl } from '../../../utils/mediaUrl';

// Ratio of tab extent to cell size (tab bulges this fraction beyond the cell).
const TAB_RATIO = 0.22;
// Snap threshold in slide-% units — how close a dragged piece must be to its
// correct slot before it locks in.
const SNAP_THRESHOLD_PCT = 4;

// Deterministic sign per interior edge, from a numeric seed. hEdges[r][c] is
// the horizontal edge between row r and row r+1 at column c. vEdges[r][c] is
// the vertical edge between column c and c+1 at row r. Sign is defined from
// the upper/left piece's perspective: +1 = tab protrudes outward, -1 = blank.
const buildEdgeSigns = (rows, cols, seed) => {
  const rand = (n) => {
    const x = Math.sin(seed * 999 + n) * 10000;
    return (x - Math.floor(x)) > 0.5 ? 1 : -1;
  };
  const hEdges = [];
  for (let r = 0; r < rows - 1; r++) {
    const row = [];
    for (let c = 0; c < cols; c++) row.push(rand(r * 131 + c));
    hEdges.push(row);
  }
  const vEdges = [];
  for (let r = 0; r < rows; r++) {
    const row = [];
    for (let c = 0; c < cols - 1; c++) row.push(rand(9973 + r * 131 + c));
    vEdges.push(row);
  }
  return { hEdges, vEdges };
};

// Get edge signs from a specific piece's perspective. 0 = flat outer border.
// Neighbouring pieces share the same physical curve, so signs invert across
// the shared edge (see buildEdgeSigns).
const pieceEdges = (row, col, rows, cols, { hEdges, vEdges }) => ({
  top:    row === 0            ? 0 : -hEdges[row - 1][col],
  right:  col === cols - 1     ? 0 :  vEdges[row][col],
  bottom: row === rows - 1     ? 0 :  hEdges[row][col],
  left:   col === 0            ? 0 : -vEdges[row][col - 1],
});

// Draw one perimeter edge from (sx,sy) to (ex,ey). tab: 0 = flat, +1 = tab
// bulges outward (perpendicular to edge, clockwise-outward normal), -1 = blank.
// Coordinates are in normalized cell units (cell = 1×1).
const drawEdge = (sx, sy, ex, ey, tab) => {
  if (tab === 0) return `L ${ex} ${ey}`;
  const dx = ex - sx;
  const dy = ey - sy;
  // Outward normal for clockwise perimeter traversal.
  const px = dy;
  const py = -dx;
  const T = TAB_RATIO * tab;
  const at = (f) => [sx + dx * f, sy + dy * f];
  const [a1x, a1y] = at(0.32);
  const [a2x, a2y] = at(0.42);
  const [b2x, b2y] = at(0.58);
  const [b1x, b1y] = at(0.68);
  const midX = sx + dx * 0.5 + px * T;
  const midY = sy + dy * 0.5 + py * T;
  const c1x = a2x + px * T * 1.4;
  const c1y = a2y + py * T * 1.4;
  const c2x = midX - dx * 0.14;
  const c2y = midY - dy * 0.14;
  const c3x = midX + dx * 0.14;
  const c3y = midY + dy * 0.14;
  const c4x = b2x + px * T * 1.4;
  const c4y = b2y + py * T * 1.4;
  return [
    `L ${a1x} ${a1y}`,
    `L ${a2x} ${a2y}`,
    `C ${c1x} ${c1y}, ${c2x} ${c2y}, ${midX} ${midY}`,
    `C ${c3x} ${c3y}, ${c4x} ${c4y}, ${b2x} ${b2y}`,
    `L ${b1x} ${b1y}`,
    `L ${ex} ${ey}`,
  ].join(' ');
};

// Build the SVG path for a single jigsaw piece, in normalized cell units.
// Cell occupies (TAB_RATIO, TAB_RATIO) → (1+TAB_RATIO, 1+TAB_RATIO) inside a
// viewBox of size (1 + 2*TAB_RATIO). Perimeter walks clockwise so the outward
// normal is consistent for tab direction math.
const buildPiecePath = (edges) => {
  const t = TAB_RATIO;
  const x0 = t;
  const y0 = t;
  const x1 = t + 1;
  const y1 = t + 1;
  return [
    `M ${x0} ${y0}`,
    drawEdge(x0, y0, x1, y0, edges.top),
    drawEdge(x1, y0, x1, y1, edges.right),
    drawEdge(x1, y1, x0, y1, edges.bottom),
    drawEdge(x0, y1, x0, y0, edges.left),
    'Z',
  ].join(' ');
};

// Scatter piece initial positions in a ring around the frame so nothing lands
// inside it. If the frame is near a slide edge we push the ring inward. Used
// as a fallback when the teacher hasn't manually placed pieces yet.
const autoScatter = (rows, cols, frame, pieceSize) => {
  const centerX = frame.x + frame.width / 2;
  const centerY = frame.y + frame.height / 2;
  const total = rows * cols;
  const positions = [];
  for (let i = 0; i < total; i++) {
    const angle = (i / total) * Math.PI * 2;
    // Ring radius (in slide %) — outside the frame, inside slide bounds.
    const rx = frame.width * 0.65 + 8;
    const ry = frame.height * 0.65 + 8;
    let px = centerX + Math.cos(angle) * rx - pieceSize.width / 2;
    let py = centerY + Math.sin(angle) * ry - pieceSize.height / 2;
    px = Math.max(0, Math.min(100 - pieceSize.width, px));
    py = Math.max(0, Math.min(100 - pieceSize.height, py));
    const row = Math.floor(i / cols);
    const col = i % cols;
    positions.push({ row, col, x: px, y: py });
  }
  return positions;
};

const DEFAULT_FRAME = { x: 30, y: 20, width: 40, height: 40 };

const PuzzleElement = ({
  element,
  isEditor = false,
  studentView = false,
  updateElementContent,
  saveToHistory,
}) => {
  const { t } = useTranslation();
  const { content, id } = element;
  const {
    imageUrl = '',
    rows = 3,
    cols = 3,
    seed = 1,
    frame = DEFAULT_FRAME,
    pieces = null,
    borderColor = '#2A4D25',
  } = content || {};

  const rootRef = useRef(null);
  const [imgAspect, setImgAspect] = useState(null); // width / height

  // Slide is 16:9. Frame height in slide-% is derived from width to preserve
  // the image aspect once we know it. Falls back to configured height when the
  // image hasn't loaded yet.
  const effectiveFrame = useMemo(() => {
    if (!imgAspect) return frame;
    // frame.width and frame.height are in % of slide. To preserve image
    // aspect in actual pixels: (frame.width * slideW) / (frame.height * slideH) = imgAspect
    // slideW/slideH = 16/9, so frame.height = frame.width * (16/9) / imgAspect.
    const desiredHeight = (frame.width * (16 / 9)) / imgAspect;
    return { ...frame, height: desiredHeight };
  }, [frame, imgAspect]);

  const edgeSigns = useMemo(() => buildEdgeSigns(rows, cols, seed), [rows, cols, seed]);

  // A single piece SVG viewBox is (1 + 2*TAB_RATIO) in each dimension. In
  // slide-% units: pieceW% = frame.width * (1 + 2*TAB_RATIO) / cols.
  const pieceSize = useMemo(() => ({
    width:  effectiveFrame.width  * (1 + 2 * TAB_RATIO) / cols,
    height: effectiveFrame.height * (1 + 2 * TAB_RATIO) / rows,
  }), [effectiveFrame.width, effectiveFrame.height, cols, rows]);

  // Correct top-left of a piece's SVG bounding box, in slide-%. Accounts for
  // the tab area (which sits outside the actual cell) on top and left edges.
  const placedTopLeft = useCallback((row, col) => ({
    x: effectiveFrame.x + (col / cols) * effectiveFrame.width  - (TAB_RATIO / cols) * effectiveFrame.width,
    y: effectiveFrame.y + (row / rows) * effectiveFrame.height - (TAB_RATIO / rows) * effectiveFrame.height,
  }), [effectiveFrame, rows, cols]);

  // Fill in missing/legacy piece positions with an auto-scatter around the
  // frame. Keeps whatever the teacher has manually placed.
  const resolvedPieces = useMemo(() => {
    const map = new Map();
    if (Array.isArray(pieces)) {
      pieces.forEach((p) => {
        if (typeof p?.row === 'number' && typeof p?.col === 'number') {
          map.set(`${p.row}-${p.col}`, p);
        }
      });
    }
    const scatter = autoScatter(rows, cols, effectiveFrame, pieceSize);
    return scatter.map((s) => map.get(`${s.row}-${s.col}`) || s);
  }, [pieces, rows, cols, effectiveFrame, pieceSize]);

  // Student-side placement state — which pieces have snapped correctly into
  // the frame. Kept internal; no external sync in v1.
  const [placed, setPlaced] = useState(() => ({}));
  const [showComplete, setShowComplete] = useState(false);
  // Live drag offset per piece (in slide-%) while pointer is down.
  const [dragOffset, setDragOffset] = useState(null); // { key, dx, dy }
  const dragRef = useRef(null);

  // Reset student state whenever the puzzle definition changes.
  useEffect(() => {
    setPlaced({});
    setShowComplete(false);
  }, [id, rows, cols, imageUrl, seed]);

  const totalPieces = rows * cols;
  const placedCount = Object.values(placed).filter(Boolean).length;
  const allPlaced = !isEditor && placedCount === totalPieces && totalPieces > 0;

  // Trigger the whole-image reveal flash once all pieces land.
  useEffect(() => {
    if (allPlaced) {
      setShowComplete(true);
    }
  }, [allPlaced]);

  // Pointer-based drag works for both mouse and touch. Editor and student
  // modes share the same handler, differing only in what they update on drop.
  const beginDrag = useCallback((e, row, col) => {
    if (!rootRef.current) return;
    if (!isEditor) {
      // Student mode: don't allow dragging already-placed pieces.
      const key = `${row}-${col}`;
      if (placed[key]) return;
    }
    e.preventDefault();
    e.stopPropagation();
    // Capture pointer so we keep receiving events if it leaves the SVG bounds.
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch (_) {}
    const rect = rootRef.current.getBoundingClientRect();
    dragRef.current = {
      key: `${row}-${col}`,
      row,
      col,
      pointerId: e.pointerId,
      startClientX: e.clientX,
      startClientY: e.clientY,
      slideWidth: rect.width,
      slideHeight: rect.height,
      moved: false,
    };
    setDragOffset({ key: `${row}-${col}`, dx: 0, dy: 0 });
  }, [isEditor, placed]);

  useEffect(() => {
    const onMove = (e) => {
      const drag = dragRef.current;
      if (!drag || e.pointerId !== drag.pointerId) return;
      const dxPct = ((e.clientX - drag.startClientX) / drag.slideWidth) * 100;
      const dyPct = ((e.clientY - drag.startClientY) / drag.slideHeight) * 100;
      if (!drag.moved && (Math.abs(dxPct) > 0.15 || Math.abs(dyPct) > 0.15)) {
        drag.moved = true;
      }
      setDragOffset({ key: drag.key, dx: dxPct, dy: dyPct });
    };

    const onUp = (e) => {
      const drag = dragRef.current;
      if (!drag || e.pointerId !== drag.pointerId) return;
      dragRef.current = null;
      const dxPct = ((e.clientX - drag.startClientX) / drag.slideWidth) * 100;
      const dyPct = ((e.clientY - drag.startClientY) / drag.slideHeight) * 100;
      setDragOffset(null);

      const initial = resolvedPieces.find((p) => p.row === drag.row && p.col === drag.col);
      if (!initial) return;
      const droppedX = initial.x + dxPct;
      const droppedY = initial.y + dyPct;

      if (isEditor) {
        // Persist the piece's new starting position.
        if (!drag.moved) return;
        const clamped = {
          x: Math.max(0, Math.min(100 - pieceSize.width,  droppedX)),
          y: Math.max(0, Math.min(100 - pieceSize.height, droppedY)),
        };
        const nextPieces = resolvedPieces.map((p) =>
          p.row === drag.row && p.col === drag.col ? { ...p, x: clamped.x, y: clamped.y } : p
        );
        saveToHistory?.();
        updateElementContent?.({ ...content, pieces: nextPieces });
        return;
      }

      // Student: snap to correct spot if close enough, else return to initial.
      const target = placedTopLeft(drag.row, drag.col);
      const distX = droppedX - target.x;
      const distY = droppedY - target.y;
      const dist = Math.hypot(distX, distY);
      if (dist <= SNAP_THRESHOLD_PCT) {
        setPlaced((prev) => ({ ...prev, [drag.key]: true }));
      }
      // Wrong drop: just release the drag offset — piece renders at initial
      // teacher-defined position (drag offset was cleared above).
    };

    window.addEventListener('pointermove', onMove, { passive: false });
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
    };
  }, [content, isEditor, placedTopLeft, pieceSize, resolvedPieces, saveToHistory, updateElementContent]);

  // Frame drag/resize (editor only). Mirrors the DragLabelBlank palette logic.
  const [frameInteract, setFrameInteract] = useState(null);
  const beginFrameInteract = (e, mode) => {
    if (!isEditor || !updateElementContent) return;
    if (!rootRef.current) return;
    if (mode.startsWith('resize-')) e.stopPropagation();
    e.preventDefault();
    const rect = rootRef.current.getBoundingClientRect();
    setFrameInteract({
      mode,
      startMouseX: e.clientX,
      startMouseY: e.clientY,
      startFrame: { ...effectiveFrame },
      slideWidth: rect.width,
      slideHeight: rect.height,
      moved: false,
    });
  };

  useEffect(() => {
    if (!frameInteract || !updateElementContent) return;
    const MIN_W = 12;
    const MIN_H = 8;
    const onMove = (e) => {
      const dxPct = ((e.clientX - frameInteract.startMouseX) / frameInteract.slideWidth) * 100;
      const dyPct = ((e.clientY - frameInteract.startMouseY) / frameInteract.slideHeight) * 100;
      const s = frameInteract.startFrame;
      let next = { ...s };
      if (frameInteract.mode === 'drag') {
        next.x = Math.max(0, Math.min(100 - s.width, s.x + dxPct));
        next.y = Math.max(0, Math.min(100 - s.height, s.y + dyPct));
      } else if (frameInteract.mode === 'resize-se') {
        // Width-driven resize: height follows via effectiveFrame math on next
        // render (imgAspect locks the ratio). We just push width and let the
        // memo recompute height.
        const nextW = Math.max(MIN_W, Math.min(100 - s.x, s.width + dxPct));
        next.width = nextW;
        // Optimistic height so the visual updates smoothly during drag; will
        // be reconciled on next render when imgAspect is applied.
        if (imgAspect) {
          const desiredH = (nextW * (16 / 9)) / imgAspect;
          next.height = Math.max(MIN_H, Math.min(100 - s.y, desiredH));
        } else {
          next.height = Math.max(MIN_H, Math.min(100 - s.y, s.height + dyPct));
        }
      }
      updateElementContent({
        ...content,
        frame: { x: next.x, y: next.y, width: next.width, height: next.height },
      });
      if (!frameInteract.moved && (Math.abs(dxPct) > 0.2 || Math.abs(dyPct) > 0.2)) {
        setFrameInteract((prev) => (prev ? { ...prev, moved: true } : prev));
      }
    };
    const onUp = () => {
      if (frameInteract.moved) saveToHistory?.();
      setFrameInteract(null);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [frameInteract, content, updateElementContent, saveToHistory, imgAspect]);

  const handleRetry = () => {
    setPlaced({});
    setShowComplete(false);
  };

  const resolvedImgUrl = resolveMediaUrl(imageUrl);

  const renderPiece = (row, col, initial) => {
    const key = `${row}-${col}`;
    const isPlaced = !isEditor && placed[key];
    const isDragging = dragOffset && dragOffset.key === key;

    let x = initial.x;
    let y = initial.y;
    if (isPlaced) {
      const target = placedTopLeft(row, col);
      x = target.x;
      y = target.y;
    }
    if (isDragging) {
      x += dragOffset.dx;
      y += dragOffset.dy;
    }

    const edges = pieceEdges(row, col, rows, cols, edgeSigns);
    const path = buildPiecePath(edges);
    const viewSize = 1 + 2 * TAB_RATIO;
    const clipId = `puzzle-clip-${id}-${row}-${col}`;

    const draggable = isEditor || (!isPlaced && !showComplete);

    return (
      <div
        key={key}
        onPointerDown={draggable ? (e) => beginDrag(e, row, col) : undefined}
        className="absolute"
        style={{
          left: `${x}%`,
          top: `${y}%`,
          width: `${pieceSize.width}%`,
          height: `${pieceSize.height}%`,
          pointerEvents: draggable ? 'auto' : 'none',
          touchAction: draggable ? 'none' : undefined,
          cursor: draggable ? (isDragging ? 'grabbing' : 'grab') : 'default',
          userSelect: 'none',
          WebkitUserSelect: 'none',
          zIndex: isDragging ? 50 : (isPlaced ? 5 : 10),
          filter: isDragging ? 'drop-shadow(0 6px 10px rgba(0,0,0,0.35))' : 'drop-shadow(0 2px 3px rgba(0,0,0,0.25))',
          transition: isDragging ? 'none' : 'left 0.18s ease, top 0.18s ease',
        }}
      >
        {resolvedImgUrl ? (
          <svg
            viewBox={`0 0 ${viewSize} ${viewSize}`}
            width="100%"
            height="100%"
            preserveAspectRatio="none"
            style={{ display: 'block', overflow: 'visible' }}
          >
            <defs>
              <clipPath id={clipId} clipPathUnits="userSpaceOnUse">
                <path d={path} />
              </clipPath>
            </defs>
            <image
              href={resolvedImgUrl}
              x={TAB_RATIO - col}
              y={TAB_RATIO - row}
              width={cols}
              height={rows}
              preserveAspectRatio="none"
              clipPath={`url(#${clipId})`}
            />
            <path
              d={path}
              fill="none"
              stroke="rgba(0,0,0,0.35)"
              strokeWidth={0.8}
              vectorEffect="non-scaling-stroke"
            />
          </svg>
        ) : (
          <div className="w-full h-full bg-gray-200 rounded" />
        )}
      </div>
    );
  };

  return (
    <div
      ref={rootRef}
      className="w-full h-full relative"
      style={{ pointerEvents: 'none' }}
    >
      {/* Target frame — only outline shown, no image hint (per design). */}
      <div
        onMouseDown={isEditor ? (e) => beginFrameInteract(e, 'drag') : undefined}
        className={`absolute rounded ${isEditor ? 'cursor-move' : ''}`}
        style={{
          left: `${effectiveFrame.x}%`,
          top: `${effectiveFrame.y}%`,
          width: `${effectiveFrame.width}%`,
          height: `${effectiveFrame.height}%`,
          border: `2px dashed ${borderColor}`,
          backgroundColor: showComplete ? 'transparent' : 'rgba(255,255,255,0.35)',
          pointerEvents: isEditor ? 'auto' : 'none',
          zIndex: 1,
        }}
      >
        {isEditor && updateElementContent && (
          <div
            onMouseDown={(e) => beginFrameInteract(e, 'resize-se')}
            className="absolute w-3 h-3 bg-white border-2 border-brand-primary rounded-sm shadow"
            style={{ bottom: -6, right: -6, cursor: 'nwse-resize', pointerEvents: 'auto' }}
          />
        )}
      </div>

      {/* Full-image reveal that fades in once every piece is placed. Sits on
          top of the piece layer so seams disappear behind a clean image. */}
      {showComplete && resolvedImgUrl && (
        <div
          className="absolute pointer-events-none"
          style={{
            left: `${effectiveFrame.x}%`,
            top: `${effectiveFrame.y}%`,
            width: `${effectiveFrame.width}%`,
            height: `${effectiveFrame.height}%`,
            zIndex: 40,
            animation: 'puzzleReveal 0.6s ease-out',
          }}
        >
          <img
            src={resolvedImgUrl}
            alt=""
            style={{ width: '100%', height: '100%', objectFit: 'fill', display: 'block' }}
          />
        </div>
      )}

      {/* Hidden image just to grab natural aspect ratio for frame sizing. */}
      {resolvedImgUrl && (
        <img
          src={resolvedImgUrl}
          alt=""
          onLoad={(e) => {
            const n = e.currentTarget;
            if (n.naturalWidth && n.naturalHeight) {
              setImgAspect(n.naturalWidth / n.naturalHeight);
            }
          }}
          style={{ position: 'absolute', width: 1, height: 1, opacity: 0, pointerEvents: 'none' }}
        />
      )}

      {/* Piece layer — one absolute-positioned SVG per piece. */}
      {resolvedPieces.map((p) => renderPiece(p.row, p.col, p))}

      {/* Empty-state hint when the teacher hasn't uploaded an image yet. */}
      {!resolvedImgUrl && (
        <div
          className="absolute flex flex-col items-center justify-center gap-2 text-gray-500 bg-white/85 rounded-lg shadow-sm px-4 py-3"
          style={{
            left: `${effectiveFrame.x + effectiveFrame.width / 2}%`,
            top: `${effectiveFrame.y + effectiveFrame.height / 2}%`,
            transform: 'translate(-50%, -50%)',
            pointerEvents: 'none',
            zIndex: 20,
          }}
        >
          <ImagePlus size={22} />
          <p className="text-xs text-center max-w-[160px]">
            {t('games.puzzle.uploadImageHint')}
          </p>
        </div>
      )}

      {/* Student action bar — retry button once completed, and progress chip. */}
      {!isEditor && resolvedImgUrl && (
        <div
          className="absolute bottom-2 right-2 flex items-center gap-2"
          style={{ pointerEvents: 'auto', zIndex: 60 }}
        >
          {showComplete && (
            <button
              onClick={handleRetry}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-200 hover:bg-gray-50 rounded-lg shadow-sm transition"
            >
              <RotateCcw size={14} />
              {t('games.common.tryAgain')}
            </button>
          )}
          {!showComplete && placedCount > 0 && (
            <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-white/85 text-gray-700 text-xs font-medium shadow-sm">
              {t('games.puzzle.progress', { done: placedCount, total: totalPieces })}
            </div>
          )}
        </div>
      )}

      <style>{`
        @keyframes puzzleReveal {
          from { opacity: 0; transform: scale(0.985); }
          to   { opacity: 1; transform: scale(1); }
        }
      `}</style>
    </div>
  );
};

export default PuzzleElement;
